import { getPageOrder, mergeRegeneratedPageOrder } from './webDataPageOrder';

/**
 * Régénération d'un site : replie les pages statiques de l'ancien web_data
 * dans les données fraîchement parsées (la régénération écrase les pages
 * proposal, mais ne doit jamais perdre les pages statiques de l'utilisateur).
 *
 * Règles (caractérisées par les tests) :
 *  - une page créée dans le site (`userCreated`) survit toujours ;
 *  - une page que le template fournit repart du template : sans cela, une
 *    correction faite dans le modèle — brancher une variable sur un contenu
 *    externe, par exemple — n'atteignait jamais un site déjà généré, alors que
 *    le squelette et les pages proposal, eux, se mettaient à jour ;
 *  - une ancienne page statique que le template ne fournit plus est conservée
 *    tant qu'on ne change pas de template ; au changement, elle disparaît si
 *    elle venait de l'ANCIEN template (`oldTemplateStaticSlugs`), et faute de
 *    connaître celui-ci (fetch échoué → null) seules les `userCreated` restent.
 *
 * @param {object} regenData - web_data fraîchement parsé (nouveau template inclus)
 * @param {object|null} previousWebData - web_data avant régénération
 * @param {{ switchedTemplate?: boolean, oldTemplateStaticSlugs?: Set<string>|null }} options
 * @returns {object} web_data fusionné
 */
export function preserveStaticPagesOnRegen(regenData, previousWebData, {
  switchedTemplate = false,
  oldTemplateStaticSlugs = null,
} = {}) {
  if (!previousWebData?.pages) return regenData;

  const staticEntries = Object.entries(previousWebData.pages)
    .filter(([, p]) => p.type === 'static')
    .filter(([slug, p]) => {
      if (p.userCreated === true) return true;
      // Le template fournit cette page : c'est sa version qui fait foi.
      if (regenData?.pages?.[slug]) return false;
      if (!switchedTemplate) return true;
      if (oldTemplateStaticSlugs == null) return false;
      return !oldTemplateStaticSlugs.has(slug);
    });

  const staticPages = Object.fromEntries(staticEntries);
  const preservedStaticOrder = getPageOrder(previousWebData)
    .filter((slug) => staticPages[slug]);

  return {
    ...regenData,
    pages: { ...regenData.pages, ...staticPages },
    pageOrder: mergeRegeneratedPageOrder(regenData, preservedStaticOrder),
  };
}
